// register.component.ts
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  ValidatorFn,
  ValidationErrors,
  AbstractControl,
} from '@angular/forms';
import { AuthService } from '../../../core/auth/auth.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { GlobalMtmUserDTO } from '../../../model/user.register';
import { delay } from 'rxjs';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.css'],
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, RouterLink],
})
export class RegisterComponent implements OnInit, OnDestroy {
  registerForm: any;
  errorMessage = '';
  isLoading = false;
  isRegisterSucessfully = false;
  countdown = 8;
  private countdownInterval: ReturnType<typeof setInterval> | null = null;

  // Invite flow state
  isInviteFlow = false;
  inviteToken = '';
  inviteClinicName = '';
  tokenValid = true;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.inviteToken = this.route.snapshot.queryParamMap.get('token') ?? '';

    if (this.inviteToken) {
      this.isInviteFlow = true;
      this.initInviteForm();
    } else {
      this.initStandardForm();
    }
  }

  private initStandardForm(): void {
    this.registerForm = this.fb.group(
      {
        clinicId: ['', Validators.required],
        name: ['', Validators.required],
        organization: ['', Validators.required],
        phone: ['', [Validators.required]],
        email: ['', [Validators.required, Validators.email]],
        password: ['', [Validators.required, Validators.minLength(8), passwordStrengthValidator()]],
        confirmPassword: ['', Validators.required],
        agreeTerms: [false, Validators.requiredTrue],
      },
      { validators: this.passwordMatchValidator },
    );
  }

  private initInviteForm(): void {
    // Decode JWT claims for display only (the server re-validates the token on submit).
    // The invite link carries base64(JWT): decode that once to obtain the raw JWT string,
    // then base64url-decode its payload segment. Both steps are UTF-8 aware so non-ASCII
    // names/clinics render correctly instead of as mojibake.
    let name = '';
    let email = '';
    let clinicCode = '';

    try {
      const rawJwt = this.decodeBase64Utf8(this.inviteToken);
      const payloadSegment = rawJwt.split('.')[1];
      if (!payloadSegment) {
        this.tokenValid = false;
        return;
      }
      const claims = JSON.parse(this.decodeBase64Utf8(payloadSegment));
      name = claims.name ?? '';
      email = claims.email ?? '';
      clinicCode = claims.clinicId ?? '';
      this.inviteClinicName = clinicCode;
    } catch {
      this.tokenValid = false;
      return;
    }

    this.registerForm = this.fb.group(
      {
        // Pre-filled and blocked
        clinicId: [{ value: clinicCode, disabled: true }],
        name: [{ value: name, disabled: true }],
        email: [{ value: email, disabled: true }],
        // Editable
        organization: ['', Validators.required],
        phone: ['', [Validators.required]],
        password: ['', [Validators.required, Validators.minLength(8), passwordStrengthValidator()]],
        confirmPassword: ['', Validators.required],
        agreeTerms: [false, Validators.requiredTrue],
      },
      { validators: this.passwordMatchValidator },
    );
  }

  // Decodes a base64 / base64url string into a UTF-8 string. Restores url-safe chars and
  // missing padding, then reinterprets the decoded bytes as UTF-8 (atob alone yields Latin-1,
  // which corrupts accented characters).
  private decodeBase64Utf8(value: string): string {
    let b64 = (value ?? '').replace(/-/g, '+').replace(/_/g, '/');
    const padLength = (4 - (b64.length % 4)) % 4;
    b64 = b64.padEnd(b64.length + padLength, '=');
    const binary = atob(b64);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return new TextDecoder('utf-8').decode(bytes);
  }

  passwordMatchValidator(form: FormGroup) {
    const password = form.get('password')?.value;
    const confirmPassword = form.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { mismatch: true };
  }

  onSubmit() {
    if (this.registerForm.invalid) {
      this.registerForm.markAllAsTouched();
      return;
    }

    if (this.isLoading) return;
    this.isLoading = true;

    if (this.isInviteFlow) {
      this.submitTherapistInvite();
    } else {
      this.submitStandardRegister();
    }
  }

  ngOnDestroy(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  private startRedirectCountdown(): void {
    this.countdown = 8;
    this.countdownInterval = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        clearInterval(this.countdownInterval!);
        this.router.navigate(['/']);
      }
    }, 1000);
  }

  private submitTherapistInvite(): void {
    const { organization, phone, password, agreeTerms } = this.registerForm.getRawValue();

    this.authService.registerTherapistViaInvite({
      inviteToken: this.inviteToken,
      password,
      organization,
      phone,
      personalData: agreeTerms ? 'Yes' : 'No',
    }).subscribe({
      next: () => {
        this.isLoading = false;
        this.isRegisterSucessfully = true;
        this.startRedirectCountdown();
      },
      error: (error: Error) => {
        this.isLoading = false;
        this.errorMessage = error.message || 'Registration failed. Please try again.';
      },
    });
  }

  private submitStandardRegister(): void {
    const formValues = this.registerForm.value;

    const user: GlobalMtmUserDTO = {
      name: formValues.name,
      clinicId: formValues.clinicId,
      eventType: 'create',
      email: formValues.email,
      password: formValues.password,
      role: 'basic',
      accountEnabled: 'true',
      APP_GlobalMTM: 'Yes',
      organization: formValues.organization,
      phone: formValues.phone,
      personalData: formValues.agreeTerms ? 'Yes' : 'No',
      countryCode: 'US',
      cultureCode: 'en-US',
    };

    this.authService
      .register(user)
      .pipe(delay(3000))
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.isRegisterSucessfully = true;
          this.startRedirectCountdown();
        },
        error: (error) => {
          console.error('Registration failed:', error);
          this.isLoading = false;
          this.errorMessage = error || 'Registration failed. Please try again.';
        },
      });
  }
}

export function passwordStrengthValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value: string = control.value || '';

    if (!value) {
      return null; // Let required validator handle empty values
    }

    const hasUpperCase = /[A-Z]+/.test(value);
    const hasLowerCase = /[a-z]+/.test(value);
    const hasNumeric = /[0-9]+/.test(value);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]+/.test(value);
    const isValidLength = value.length >= 8;

    const passwordValid =
      hasUpperCase &&
      hasLowerCase &&
      hasNumeric &&
      hasSpecialChar &&
      isValidLength;

    return !passwordValid
      ? {
          passwordStrength: {
            hasUpperCase,
            hasLowerCase,
            hasNumeric,
            hasSpecialChar,
            isValidLength,
          },
        }
      : null;
  };
}
